// src/pages/invoices/components/ItemModal.tsx
import SelectDropdown from '@/components/common/SelectDropdown';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { taxPreferences } from '@/lib/constants/common';
import { itemTypes } from '@/lib/constants/items';
import { itemSchema } from '@/lib/schemas/item.schema';
import { itemsAPI } from '@/lib/utils/api';
import { Item } from '@/types/items.types';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { toast } from 'react-toastify';

interface ItemFormData {
  itemType: 'service' | 'goods';
  itemName: string;
  sacCode: string;
  hsnCode: string;
  taxPreference: string;
  sellingPrice: string | number;
  currency: string;
  description: string;
}

interface ItemModalProps {
  open: boolean;
  onClose: () => void;
  onItemAdded: (item: Item) => void;
  invoiceCurrency: string;
}

const ItemModal = ({ open, onClose, onItemAdded, invoiceCurrency }: ItemModalProps) => {
  const [loading, setLoading] = useState(false);

  const initialValues: ItemFormData = {
    itemType: 'service',
    itemName: '',
    sacCode: '',
    hsnCode: '',
    taxPreference: 'taxable',
    sellingPrice: '',
    currency: invoiceCurrency || 'INR',
    description: '',
  };

  const handleSubmit = async (
    values: ItemFormData,
    { setSubmitting }: FormikHelpers<ItemFormData>
  ) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        sellingPrice: values.sellingPrice ? Number(values.sellingPrice) : 0,
        sacCode: values.itemType === 'service' ? values.sacCode : '',
        hsnCode: values.itemType === 'goods' ? values.hsnCode : '',
      };

      const response = await itemsAPI.create(payload);
      toast.success('Item created successfully');
      onItemAdded(response.data?.item || response.data);
      // onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create item');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Item</DialogTitle>
        </DialogHeader>

        <Formik
          initialValues={initialValues}
          validationSchema={itemSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Item Type */}
                <div className="space-y-2">
                  <Label>Item Type *</Label>
                  <div className="space-y-1">
                    <SelectDropdown
                      value={values.itemType}
                      options={itemTypes}
                      onChange={(val) => {
                        setFieldValue('itemType', val);
                        if (val === 'service') {
                          setFieldValue('hsnCode', '');
                        } else {
                          setFieldValue('sacCode', '');
                        }
                      }}
                    />
                    {touched.itemType && errors.itemType && (
                      <p className="text-sm text-red-500">{errors.itemType}</p>
                    )}
                  </div>
                </div>

                {/* Item Name */}
                <div className="space-y-2">
                  <Label>Item Name *</Label>
                  <div className="space-y-1">
                    <Input
                      name="itemName"
                      value={values.itemName}
                      placeholder="Enter item name"
                      onChange={handleChange}
                    />
                    {touched.itemName && errors.itemName && (
                      <p className="text-sm text-red-500">{errors.itemName}</p>
                    )}
                  </div>
                </div>

                {/* SAC/HSN Code */}
                {values.itemType === 'service' ? (
                  <div className="space-y-2">
                    <Label>SAC Code *</Label>
                    <div className="space-y-1">
                      <Input
                        name="sacCode"
                        value={values.sacCode}
                        placeholder="e.g., 998314"
                        onChange={handleChange}
                      />
                      {touched.sacCode && errors.sacCode && (
                        <p className="text-sm text-red-500">{errors.sacCode}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>HSN Code *</Label>
                    <div className="space-y-1">
                      <Input
                        name="hsnCode"
                        value={values.hsnCode}
                        placeholder="e.g., 1234"
                        onChange={handleChange}
                      />
                      {touched.hsnCode && errors.hsnCode && (
                        <p className="text-sm text-red-500">{errors.hsnCode}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Tax Preference */}
                <div className="space-y-2">
                  <Label>Tax Preference</Label>
                  <SelectDropdown
                    value={values.taxPreference}
                    options={taxPreferences}
                    onChange={(val) => setFieldValue('taxPreference', val)}
                  />
                </div>

                {/* Selling Price */}
                <div className="space-y-2">
                  <Label>Selling Price</Label>
                  <Input
                    type="number"
                    name="sellingPrice"
                    min="0"
                    step="0.01"
                    value={values.sellingPrice}
                    placeholder="Enter selling price"
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Enter item description..."
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Item'}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ItemModal;
